import { ReactNode } from 'react';

import { Endpoints } from '@/api';
import { FilterCheckboxItem } from '@/design_system/Filters/FiltersMenu/FilterCheckboxItem';
import { FilterSearch } from '@/design_system/Filters/FiltersMenu/FilterSearch';
import {
  FilterItemNoResults,
  FiltersMenu,
  FiltersSubMenu,
} from '@/design_system/Filters/FiltersMenu/FiltersMenu';
import { FilterTag } from '@/design_system/Filters/FiltersMenu/FilterTag';
import IconCloth from '@/icons/Cloth.svg';
import { type Brand, computeBrandName } from '@/models/brand';

import { useBrandFilterState } from './useBrandFilterState';

export const DesktopBrandFilter = ({
  selectedBrands,
  setSelectedBrands,
  brandsQuery,
  tag,
  label,
  searchPlaceholder,
  tagMultipleSelectionLabel,
  icon = <IconCloth />,
}: {
  selectedBrands: Brand[];
  setSelectedBrands: (brands: Brand[]) => void;
  brandsQuery: Endpoints['GET /brands']['query'];
  tag?: boolean;
  label: string;
  searchPlaceholder: string;
  tagMultipleSelectionLabel: (selectedBrandsNumber: number) => string;
  icon?: ReactNode;
}) => {
  const { search, setSearch, allBrands, filteredBrands, showSearch, hasBrands } =
    useBrandFilterState(brandsQuery);

  const selectedBrandsNumber = selectedBrands.length;

  const options =
    filteredBrands.length > 0 ? (
      filteredBrands.map((brand: Brand) => (
        <FilterCheckboxItem
          key={brand.id}
          checked={selectedBrands.some((brd) => brd.id === brand.id)}
          onChange={(checked) => {
            if (checked) {
              setSelectedBrands([...selectedBrands, brand]);
            } else {
              setSelectedBrands(selectedBrands.filter((brd) => brd.id !== brand.id));
            }
          }}
        >
          <span className="text-ellipsis">{computeBrandName(brand, allBrands)}</span>
        </FilterCheckboxItem>
      ))
    ) : (
      <FilterItemNoResults />
    );

  const searchWithOptions = (
    <FilterSearch
      inputProps={{
        ariaLabel: searchPlaceholder,
        placeholder: searchPlaceholder,
        value: search,
        onChange: setSearch,
      }}
    >
      {options}
    </FilterSearch>
  );

  const children = showSearch ? searchWithOptions : options;

  if (!hasBrands) {
    return null;
  }

  if (tag) {
    if (selectedBrandsNumber === 0) {
      return null;
    }

    return (
      <FiltersMenu
        button={
          <FilterTag
            label={label}
            value={
              selectedBrandsNumber === 1
                ? computeBrandName(selectedBrands[0], allBrands)
                : tagMultipleSelectionLabel(selectedBrandsNumber)
            }
            tooltip={
              selectedBrandsNumber > 1
                ? selectedBrands.map((brd) => computeBrandName(brd, allBrands)).join(', ')
                : undefined
            }
            onClear={() => setSelectedBrands([])}
          />
        }
      >
        {children}
      </FiltersMenu>
    );
  }

  return (
    <FiltersSubMenu icon={icon} label={label}>
      {children}
    </FiltersSubMenu>
  );
};
